import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';
import {Center, Spinner, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  useCreateSourcingVehicleForBusinessCaseMutation,
  useSourcingVehicleDetailQuery,
} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  useCreatePurchaseBusinessCaseMutation,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  CustomerWidgetCard,
  handleApiError,
  queryParams,
  RestrictedArea,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate, useOnMount} from 'shared/web';

import {handleSaleVehicleSelectionError} from '../../components/BusinessCaseOffers/BusinessCaseSaleVehicles/OfferAlternatives/handleOfferAlternativeError';
import {BusinessCaseConceptPage} from './components/BusinessCaseConceptPage';
import {
  CompleteSourcingVehicleFields,
  VehicleFields,
} from './components/CompleteSourcingVehicleFields';
import {getSourcingSellerInitialValues} from './getSourcingSellerInitialValues';

interface CreateSourcingPurchaseBusinessCaseProps {
  externalId: string;
}

interface SourcingVehicleAutoPreparationProps {
  onPrepare: () => void;
}

export function CreateSourcingPurchaseBusinessCase(props: CreateSourcingPurchaseBusinessCaseProps) {
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [completedVehicleFields, setCompletedVehicleFields] = useState<VehicleFields | null>(null);
  const navigate = useNavigate();
  const [prepareVehicle, {isError: isPreparationError, isUninitialized}] =
    useCreateSourcingVehicleForBusinessCaseMutation();
  const [createPurchaseBusinessCase, {isLoading: isCreating}] =
    useCreatePurchaseBusinessCaseMutation();
  const [getBusinessCase] = useLazyGetBusinessCaseQuery();
  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();
  const [canCreatePurchase, canAddVehicleToBuy] = usePermissions({
    permissionKeys: ['createBusinessCasePurchase', 'addVehicleToBuy'],
  });
  const {data: vehicle} = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);
  const {
    data: sourcingDetail,
    isLoading: isSellerLoading,
    isFetching: isSellerFetching,
    isError: isSellerError,
    refetch: refetchSeller,
  } = useSourcingVehicleDetailQuery(
    {externalId: props.externalId},
    {skip: !canCreatePurchase || !canAddVehicleToBuy}
  );

  const sourceMake = sourcingDetail?.sourcingVehicle.make?.key ?? '';
  const sourceModel = sourcingDetail?.sourcingVehicle.model?.key ?? '';
  const startPreparation = (fields?: VehicleFields) => {
    prepareVehicle({
      externalId: props.externalId,
      make: (fields?.make ?? completedVehicleFields?.make ?? sourceMake) || undefined,
      model: (fields?.model ?? completedVehicleFields?.model ?? sourceModel) || undefined,
    })
      .unwrap()
      .then(({id}) => setVehicleId(id))
      .catch(handleApiError);
  };

  const renderBusinessCaseConceptPage = (
    readyVehicleId: string,
    readySourcingDetail: NonNullable<typeof sourcingDetail>
  ) => {
    const onCustomer = (customer: CustomerResponseBodyV2) => {
      if (isCreating || !canCreatePurchase || !canAddVehicleToBuy) {
        return;
      }
      createPurchaseBusinessCase({
        createPurchaseBusinessCaseRequestBody: {
          customerId: customer.id,
          vehicleToPurchaseId: readyVehicleId,
          purchaseType: 'IN_STOCK',
        },
      })
        .unwrap()
        .then((businessCase) => {
          navigate(
            composePath(businessCaseRoutes.buying, {
              params: {id: businessCase.id},
              isSearchParamsPreserved: false,
            })
          );
          getBusinessCase({businessCaseId: businessCase.id})
            .unwrap()
            .then((createdBusinessCase) => {
              handleSearchAuthorizationProfiles(
                {
                  vehicleMake: vehicle?.make ?? '',
                  vehicleType: vehicle?.type ?? null,
                  vehicleModelFamily: vehicle?.vehicle?.modelFamily ?? null,
                  vehicleModelFamilyGroup: vehicle?.vehicle?.modelFamilyGroup ?? null,
                },
                customer.id,
                {bcNumber: createdBusinessCase.code, bcCreatedAt: createdBusinessCase.createdAt}
              );
            })
            .catch(handleApiError);
        })
        .catch(handleSaleVehicleSelectionError);
    };

    const customerContent = (
      <Center>
        <VStack spacing={4} maxWidth={170} width="100%">
          <CustomerWidgetCard
            customTitle={i18n.t('entity.customer.labels.customer')}
            secondStepComponentType="BUSINESS_CASE"
            onCustomer={onCustomer}
            isLoadingOnCustomer={isCreating}
            shouldWarnBeforeActiveBusinessCaseEdit
            initialValues={getSourcingSellerInitialValues(
              readySourcingDetail.sourcingVehicle.seller
            )}
            data-testid={testIds.businessCase.concept('customerWidget')}
            vehicleId={readyVehicleId}
          />
        </VStack>
      </Center>
    );

    return (
      <BusinessCaseConceptPage
        customerContent={customerContent}
        customerHref={composePath(businessCaseRoutes.concept, {
          queryParams: {[queryParams.SOURCING_EXTERNAL_ID]: props.externalId},
          isSearchParamsPreserved: false,
        })}
        sellingSnippet={{label: i18n.t('page.businessCase.labels.selling'), isDisabled: true}}
        buyingSnippet={{
          label: i18n.t('entity.businessCase.labels.buying'),
          title: vehicle?.title,
          parameters: [
            vehicle?.state?.registrationPlate ||
              i18n.t('entity.vehicle.notifications.noLicencePlate'),
            vehicle?.vin || i18n.t('entity.vehicle.notifications.noVIN'),
          ],
          href: vehicle
            ? composePath(vehiclesRoutes.detail, {
                params: {id: vehicle.id},
                isSearchParamsPreserved: false,
              })
            : undefined,
        }}
      />
    );
  };

  const renderSellerStatus = () => (
    <Center>
      <DataStatus
        isLoading={isSellerLoading || isSellerFetching}
        isError={!isSellerLoading && !isSellerFetching}
        errorAction={{title: i18n.t('general.actions.retry'), onClick: refetchSeller}}
      />
    </Center>
  );
  const needsFields =
    (sourceMake.length === 0 || sourceModel.length === 0) && completedVehicleFields === null;
  const shouldAutoPrepare = isUninitialized && isNil(completedVehicleFields);

  return match({
    canCreatePurchase,
    canAddVehicleToBuy,
    sourcingDetail,
    isSellerError,
    needsFields,
    vehicleId,
    isPreparationError,
    shouldAutoPrepare,
  })
    .with({canCreatePurchase: false}, () => <RestrictedArea />)
    .with({canAddVehicleToBuy: false}, () => <RestrictedArea />)
    .with({isSellerError: true}, renderSellerStatus)
    .with({sourcingDetail: Pattern.nullish}, renderSellerStatus)
    .with({vehicleId: null, needsFields: true}, () => (
      <CompleteSourcingVehicleFields
        make={sourceMake}
        model={sourceModel}
        onContinue={(fields) => {
          setCompletedVehicleFields(fields);
          startPreparation(fields);
        }}
      />
    ))
    .with({vehicleId: null, isPreparationError: true}, () => (
      <Center>
        <DataStatus
          isError
          errorAction={{title: i18n.t('general.actions.retry'), onClick: () => startPreparation()}}
        />
      </Center>
    ))
    .with({vehicleId: null, shouldAutoPrepare: true}, () => (
      <SourcingVehicleAutoPreparation onPrepare={() => startPreparation()} />
    ))
    .with({vehicleId: null}, () => (
      <Center>
        <Spinner />
      </Center>
    ))
    .with(
      {vehicleId: Pattern.string, sourcingDetail: Pattern.nonNullable},
      ({vehicleId, sourcingDetail}) => renderBusinessCaseConceptPage(vehicleId, sourcingDetail)
    )
    .exhaustive();
}

function SourcingVehicleAutoPreparation(props: SourcingVehicleAutoPreparationProps) {
  useOnMount(props.onPrepare);

  return (
    <Center>
      <Spinner />
    </Center>
  );
}
